import { OpenHouse } from './open-house';
import { Point } from './point';
import { Room } from './room';
import { ResoPropertyType, Provider, ResoPropertySubType, StandardStatus } from '@zoocasa/go-search';
import dayjs from 'dayjs';
import { camelizeKeys } from '@zoocasa/node-kit/objects/transform-keys';
import { AgreementTypes } from 'types/agreement-storage-types';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import { cleanDashedString } from 'data/addresses';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { standardStatusFromJSON } from 'types/reso';
import { countryCodeFromProvinceOrState } from 'utils/province_or_state';
import { SiteLanguage } from 'types/locales';
import { mlsProviderIds } from 'constants/mlsProviderIds';
import { isQuebecListing } from 'utils/is-quebec-listing';

dayjs.extend(utc);
dayjs.extend(timezone);

export type ListingSerializableProperties = ReturnType<typeof Listing.prototype.toJSON>;
export type Miscellaneous = {
  acreage?: string | null;
  approxAge?: string | null;
  balcony?: string | null;
  bldgAmenities1Out?: string | null;
  bldgAmenities2Out?: string | null;
  bldgAmenities3Out?: string | null;
  bldgAmenities4Out?: string | null;
  bldgAmenities5Out?: string | null;
  bldgAmenities6Out?: string | null;
  buildingInsuranceIncluded?: boolean | null;
  cableTvIncluded?: boolean | null;
  cacIncluded?: boolean | null;
  centralVac?: boolean | null;
  commonElementIncluded?: boolean | null;
  condoExp?: string | null;
  condoTaxesIncluded?: boolean | null;
  corpNum?: string | number | null;
  ensuiteLaundry?: boolean | null;
  elevator?: boolean | null;
  fireplaceStove?: boolean | null;
  garageSpaces?: string | null;
  garageParkSpaces?: string | null;
  laundryLevel?: string | null;
  heatIncluded?: boolean | null;
  hydroIncluded?: boolean | null;
  kitchens?: string | null;
  parkingFac?: string | null;
  parkingIncluded?: boolean | null;
  parkingLegalDesc1?: string | null;
  parkingSpaces?: string | number | null;
  parkingSpot1?: string | null;
  parkingType?: string | null;
  pets?: string | null;
  stories?: string | null;
  waterIncluded?: boolean | null;
};

export type ListingLanguage = SiteLanguage.English | SiteLanguage.French;
export type LocaleData = {
  [language in ListingLanguage]?: {
    extras?: string | null;
    description?: string | null;
  };
};

export type AgentInfo = {
  name: string;
  email: string;
  phone: string;
  photo: string;
  designation: {
    [language in ListingLanguage]?: string;
  };
  exp_member_key: number | null;
};

// Fields that should return empty string for French language
const _LOCALE_SENSITIVE_DYNAMIC_FIELDS = ['driveway', 'heat', 'basement', 'exterior', 'ac', 'heatingFuel'] as const;
type LocaleSensitiveDynamicField = typeof _LOCALE_SENSITIVE_DYNAMIC_FIELDS[number];

export default class Listing {
  id: number;
  type: string | null;
  isRental: boolean | null;
  price: number | null;
  soldPrice: number | null;
  status: string | null;
  addressPath: string | null;
  isStandardized: string;
  bedrooms: number;
  bathrooms: number;
  bedroomsPartial: number | null;
  bathroomsPartial: number | null;
  providerId: number | null;
  isVow: boolean;
  isItsoVow: boolean;
  isCrea: boolean;
  isImageReady: boolean;
  imageUrls: string[];
  thumbnailUrls: string[];
  position: Point | null;
  unitNumber: string | null;
  mlsNum: string | null;
  streetNumber: string;
  streetName: string;
  city: string | null;
  province: string;
  postalCode: string;
  openHouses: null | OpenHouse[];
  styleName: string;
  imageUrl: string | null;
  thumbnailUrl: string;
  path: string;
  parking: number | null;
  addressSlug: string;
  availableAt: string | null;
  #addedAt: Date | null;
  #expiredAt: Date | null;
  #updatedOn: Date | null;
  #soldAt: Date | null;
  lastStatus: string | null;
  squareFootage: Record<'min' | 'max', number> | null;
  virtualTourUrl: string | null;
  levels: string | null;
  locker: string | null;
  maintenanceFees: number | null;
  taxes: number | null;
  garage: string | null;
  rooms: Room[] | null;
  lotFrontage: string | null;
  lotDepth: string | null;
  style: 'house' | 'condo' | 'land';
  brokerage: string | null;
  brokerageEmail: string | null;
  brokeragePhone: string | null;
  disclaimer: string | null;
  misc: Miscellaneous | null;
  neighbourhoodName: string | null;
  url: string | null;
  imageDesc: string | null;
  termsOfUseRequired: boolean | null;
  statusId: number | null;
  imageDescs: Map<string, string> | null;
  neighbourhood: Record<'id', number> | null;
  pool: string | null;
  buyerInfo: {
    agentFirstName?: string | null;
    agentLastName?: string | null;
    officeName?: string | null;
  } | null;
  lastFetched: Date | null;
  expAgentId: number | null;
  displayAddress: boolean | null;
  standardStatus: StandardStatus | null;
  propertyType: typeof ResoPropertyType[keyof typeof ResoPropertyType] | null;
  propertySubType: typeof ResoPropertySubType[keyof typeof ResoPropertySubType] | null;
  listingAgent: string | null;
  listingAgentContact: string | null;
  originalMlsName: string | null;
  localeData: LocaleData | null;

  #language: ListingLanguage;
  #localeSensitiveDynamicFields: Map<LocaleSensitiveDynamicField, string | null>;
  #agentsInfo: AgentInfo[] | null;

  private constructor(language: ListingLanguage = SiteLanguage.English) {
    this.#language = language;
    this.#localeSensitiveDynamicFields = new Map();
    this.#agentsInfo = null;
  }

  get language() {
    return this.#language;
  }

  get isCondo() {
    return this.style === 'condo';
  }

  get isAvailable() {
    if (this.standardStatus && this.standardStatus !== StandardStatus.UNRECOGNIZED) {
      return this.standardStatus === StandardStatus.STANDARD_STATUS_ACTIVE || this.standardStatus === StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT;
    }
    // fallback to status if standardStatus is not set
    return this.status === 'available';
  }

  get isSold() {
    if (this.standardStatus && this.standardStatus !== StandardStatus.UNRECOGNIZED) {
      return this.standardStatus === StandardStatus.STANDARD_STATUS_CLOSED;
    }
    // fallback to status if standardStatus is not set
    return this.status === 'not_available_sold';
  }

  get isInactive() {
    return !this.isAvailable && !this.isSold;
  }

  get isSoldWithoutSoldPrice() {
    return this.isSold && !this.soldPrice;
  }

  get avgSqft() {
    const { min, max } = this.squareFootage || {};
    if (min && max) {
      return Math.ceil((min + max) / 2);
    } else if (min) {
      return min;
    } else if (max) {
      return max;
    } else {
      return null;
    }
  }

  /** @deprecated use `src/types/reso.ts#getStatusLabelKey` instead */
  get statusLabel() {
    let lastStatusLabel: string | null = null;
    if (this.lastStatus) {
      const lastStatusMappings: Record<string, string> = {
        Bom: 'Back on Market',
        Dft: 'Deal Fell Through',
        Exp: 'Expired',
        Ext: 'Extended',
        Lc: 'Leased Conditionally',
        Lsd: 'Leased',
        New: 'New',
        Pc: 'Price Change',
        Sc: 'Sold Conditionally',
        Sld: 'Sold',
        Sus: 'Suspended',
        Ter: 'Terminated',
      };
      lastStatusLabel = lastStatusMappings[this.lastStatus];
    }
    if (lastStatusLabel && lastStatusLabel !== 'New') {
      return lastStatusLabel;
    } else if (this.isAvailable) {
      return this.isRental ? 'For Rent' : 'For Sale';
    } else if (this.isSold) {
      return this.isRental ? 'Leased' : 'Sold';
    } else {
      return 'Inactive';
    }
  }

  get isPrivate() {
    return this.isVow;
  }

  get isPillar9Vow() {
    return this.providerId === Provider.PROVIDER_PILLAR9_VOW;
  }

  get isRebgvVow() {
    return this.providerId === Provider.PROVIDER_REBGV_VOW;
  }

  get isVancouverFeed() {
    return this.providerId === Provider.PROVIDER_REBGV_VOW || this.providerId === Provider.PROVIDER_REBGV_IDX;
  }

  get isTrebIdxInactive() {
    return this.providerId === Provider.PROVIDER_TREB_IDX && !this.isAvailable;
  }

  get miscellaneous() {
    return this.misc ? camelizeKeys(this.misc as unknown as Record<string, unknown>) as Record<string, string | number | boolean> : null;
  }

  get daysOnMarket() {
    if (this.addedAt) {
      const addedAt = dayjs(this.addedAt);
      return dayjs().diff(addedAt, 'day');
    } else {
      return null;
    }
  }

  get sqftLabel() {
    let sqft = '-';
    if (this.squareFootage) {
      const { min, max } = this.squareFootage;
      if (!min && max === 0 || !max && min === 0) {
        return sqft;
      }
      // Note: if max sqft is 499 always show 0-499, for anything else show min-max or just min or max when only one available
      sqft = max === 499 ? '0-499' : [min, max].filter(item => item).join('-');
      sqft = `${sqft} sqft`;
    }
    return sqft;
  }

  get lotSize() {
    const { lotFrontage, lotDepth } = this;
    if (lotFrontage && lotDepth) {
      return `${lotFrontage} x ${lotDepth}`;
    }
    
    // Fallback: If frontage/depth not available but we have acreage, display that
    const acreage = this.misc?.acreage;
    if (acreage) {
      const acres = parseFloat(acreage);
      if (!isNaN(acres) && acres > 0) {
        if (acres === 1) {
          return '1 acre';
        } else {
          return `${acres} acres`;
        }
      }
    }
    
    return null;
  }

  get homeType() {
    const condos = ['condo', 'condo-highrise', 'condo-lowrise', 'condo-new-development', 'condo-other'];
    const houses = ['house-detached', 'house-attached', 'house-semidetached', 'townhouse', 'house-other'];
    const styleName: string | null = this.styleName;
    if (!styleName) {
      return null;
    } else if (condos.includes(styleName)) {
      return 'condo';
    } else if (houses.includes(styleName)) {
      return styleName;
    }
  }

  get priceBySqft(): number | null {
    if (this.avgSqft && this.price) {
      return Math.floor(this.price / this.avgSqft);
    }
    return null;
  }

  get seoType() {
    let type = this.homeType?.split('-')[0];
    if (type) {
      type = type.replace(/\bhouse/i, 'House');
      return type[0].toUpperCase() + type.slice(1).toLowerCase();
    }
  }

  get addressPathWithFallback() {
    if (!this.displayAddress) {
      return this.addressPath;
    }
    return cleanDashedString(this.addressPath || this.path);
  }

  get isSignInAndTermsOfUseRequired() {
    return this.termsOfUseRequired ? true : false;
  }

  get isTreb() {
    return !this.isCrea;
  }

  get agreementType() {
    if (this.isPillar9Vow){
      return AgreementTypes.PILLAR9_VOW;
    } else if (this.isRebgvVow){
      return AgreementTypes.REBGV_VOW;
    } else if (this.isItsoVow){
      return AgreementTypes.ITSO_VOW;
    } else if (this.isCrea){
      return AgreementTypes.CREA;
    }
  }

  get isOffMarket() {
    if (this.standardStatus && this.standardStatus !== StandardStatus.UNRECOGNIZED) {
      return this.standardStatus === StandardStatus.STANDARD_STATUS_DELETE;
    }
    // fallback to status if standardStatus is not set
    return this.status === 'deleted';
  }

  getStreet = (isAuthenticated: boolean) => {
    return capitalizeWords(!isAuthenticated && this.isPrivate ? this.streetName : this.seoStreet);
  };

  get seoStreet() {
    const unitNumber = this.unitNumber || '';
    const streetAddress = this.streetAddress;
    const seoStreet = unitNumber.length > 0 ? `${unitNumber} - ${streetAddress}` : streetAddress;
    return seoStreet;
  }

  get streetAddress() {
    const { streetNumber, streetName } = this;
    return streetNumber ? `${streetNumber} ${streetName}` : streetName;
  }

  get countryCode() {
    return countryCodeFromProvinceOrState(this.province);
  }

  get extras() {
    return this.localeData?.[this.language]?.extras ?? null;
  }

  get description() {
    return this.localeData?.[this.language]?.description ?? null;
  }

  get agentsInfo(): (Omit<AgentInfo, 'designation'> & { designation: string })[] | null {
    if (!this.#agentsInfo) {
      return null;
    }
    return this.#agentsInfo.map(agent => ({
      ...agent,
      designation: agent.designation[this.language] ?? agent.designation[SiteLanguage.English] ?? '',
    }));
  }

  // Generic helper for locale-sensitive dynamic fields
  #getLocaleSensitiveDynamicField(field: LocaleSensitiveDynamicField): string | null {
    const value = this.#localeSensitiveDynamicFields.get(field);
    return this.#language === 'fr' ? '' : (value ?? null);
  }

  get driveway() {
    return this.#getLocaleSensitiveDynamicField('driveway');
  }

  get heat() {
    return this.#getLocaleSensitiveDynamicField('heat');
  }

  get basement() {
    return this.#getLocaleSensitiveDynamicField('basement');
  }

  get exterior() {
    return this.#getLocaleSensitiveDynamicField('exterior');
  }

  get ac() {
    return this.#getLocaleSensitiveDynamicField('ac');
  }

  get heatingFuel() {
    return this.#getLocaleSensitiveDynamicField('heatingFuel');
  }

  /**
   * Applies regional compliance restrictions to the listing data.
   * This method modifies the listing in place to comply with regional requirements.
   * 
   * Currently handles:
   * - Quebec: Removes all timestamps (addedAt, expiredAt, updatedOn, soldAt)
   */
  applyListingRestrictions(): void {
    if (isQuebecListing(this.province)) {
      // Remove timestamps for Quebec listings
      this.#addedAt = null;
      this.#expiredAt = null;
      this.#updatedOn = null;
      this.#soldAt = null;
    }
  }

  get addedAt(): Date | null {
    return this.#addedAt;
  }

  get expiredAt(): Date | null {
    return this.#expiredAt;
  }

  get updatedOn(): Date | null {
    return this.#updatedOn;
  }

  get soldAt(): Date | null {
    return this.#soldAt;
  }

  toJSON() {
    return {
      id: this?.id ?? null,
      type: this?.type ?? null,
      isRental: this?.isRental ?? null,
      price: this?.price ?? null,
      soldPrice: this?.soldPrice ?? null,
      status: this?.status ?? null,
      addressPath: this?.addressPath ?? null,
      isStandardized: this?.isStandardized ?? null,
      bedrooms: this?.bedrooms ?? null,
      bathrooms: this?.bathrooms ?? null,
      bedroomsPartial: this?.bedroomsPartial ?? null,
      bathroomsPartial: this?.bathroomsPartial,
      providerId: this?.providerId ?? null,
      isVow: this?.isVow ?? null,
      isItsoVow: this?.isItsoVow ?? null,
      isCrea: this?.isCrea ?? null,
      isImageReady: this?.isImageReady ?? null,
      imageUrls: this?.imageUrls ?? null,
      thumbnailUrls: this?.thumbnailUrls ?? null,
      position: this?.position?.toJSON() ?? null,
      unitNumber: this?.unitNumber,
      mlsNum: this?.mlsNum ?? null,
      streetNumber: this?.streetNumber ?? null,
      streetName: this?.streetName ?? null,
      city: this?.city ?? null,
      province: this?.province ?? null,
      postalCode: this?.postalCode ?? null,
      openHouses: this?.openHouses?.map(r => r.toJSON()) ?? null,
      styleName: this?.styleName ?? null,
      imageUrl: this?.imageUrl ?? null,
      thumbnailUrl: this?.thumbnailUrl ?? null,
      path: this?.path ?? null,
      parking: this?.parking ?? null,
      addressSlug: this?.addressSlug ?? null,
      availableAt: this?.availableAt ?? null,
      addedAt: this.#addedAt?.toISOString() ?? null,
      expiredAt: this.#expiredAt?.toISOString() ?? null,
      updatedOn: this.#updatedOn?.toISOString() ?? null,
      soldAt: this.#soldAt?.toISOString() ?? null,
      lastStatus: this?.lastStatus ?? null,
      squareFootage: this?.squareFootage ?? null,
      virtualTourUrl: this?.virtualTourUrl ?? null,
      levels: this?.levels ?? null,
      locker: this?.locker ?? null,
      maintenanceFees: this?.maintenanceFees ?? null,
      taxes: this?.taxes ?? null,
      exterior: this.#localeSensitiveDynamicFields.get('exterior') ?? null,
      garage: this?.garage ?? null,
      driveway: this.#localeSensitiveDynamicFields.get('driveway') ?? null,
      heat: this.#localeSensitiveDynamicFields.get('heat') ?? null,
      ac: this.#localeSensitiveDynamicFields.get('ac') ?? null,
      basement: this.#localeSensitiveDynamicFields.get('basement') ?? null,
      heatingFuel: this.#localeSensitiveDynamicFields.get('heatingFuel') ?? null,
      rooms: this?.rooms?.map(r => r.toJSON()) ?? null,
      lotFrontage: this?.lotFrontage ?? null,
      lotDepth: this?.lotDepth ?? null,
      style: this?.style ?? null,
      brokerage: this?.brokerage ?? null,
      brokerageEmail: this?.brokerageEmail ?? null,
      brokeragePhone: this?.brokeragePhone ?? null,
      disclaimer: this?.disclaimer ?? null,
      misc: this?.misc ?? null,
      neighbourhoodName: this?.neighbourhoodName ?? null,
      url: this?.url ?? null,
      imageDesc: this?.imageDesc ?? null,
      termsOfUseRequired: this?.termsOfUseRequired ?? null,
      statusId: this?.statusId ?? null,
      imageDescs: this?.imageDescs ? Object.fromEntries(this?.imageDescs) : null,
      neighbourhood: this?.neighbourhood ?? null,
      pool: this?.pool ?? null,
      buyerInfo: this?.buyerInfo ?? null,
      lastFetched: this?.lastFetched?.toISOString() ?? null,
      expAgentId: this?.expAgentId ?? null,
      displayAddress: this?.displayAddress ?? null,
      standardStatus: this?.standardStatus ?? null,
      propertyType: this?.propertyType ?? null,
      propertySubType: this?.propertySubType ?? null,
      listingAgent: this?.listingAgent ?? null,
      listingAgentContact: this?.listingAgentContact ?? null,
      originalMlsName: this?.originalMlsName ?? null,
      localeData: this?.localeData ?? null,
      agentsInfo: this.#agentsInfo ?? null,
      language: this?.language ?? SiteLanguage.English,
    };
  }

  /**
   * Create a listing instance from a client-app listing object (received from the client-app JSON Api).
   *
   * @param clientAppListing The client-app listing object to create a listing from.
   * @returns A new listing instance.
   */
  static fromClientAppListing(clientAppListing: ClientAppListing, language: ListingLanguage = SiteLanguage.English) {
    if (!isClientAppListing(clientAppListing)) {
      throw new Error('Invalid listing');
    }
    const listing = new Listing(language);
    const camelizedListing = camelizeKeys(clientAppListing);
    const attributes = camelizedListing.attributes as Record<string, unknown>;
    const relationships = camelizedListing.relationships as Record<string, unknown>;
    const neighbourhood = relationships?.neighbourhood as Record<string, unknown> | null;
    const neighbourhoodData = neighbourhood && 'data' in neighbourhood ? neighbourhood.data as Record<string, unknown> : {};
    const buyerInfo = attributes.buyerInfo as Record<string, string> | null ?? null;

    const hasImageDescs = !!attributes?.imageDescs;
    const imageDescsMap = hasImageDescs ? new Map() : null;
    if (hasImageDescs) {
      const imageDescs = attributes.imageDescs as Record<string, string>;
      for (const key in imageDescs) {
        if (Object.hasOwnProperty.call(imageDescs, key)) {
          (imageDescsMap as Map<string, string>).set(key, imageDescs[key]);
        }
      }
    }
    listing.id = Number(camelizedListing.id);
    listing.type = attributes.type as string | null ?? null;
    listing.isRental = attributes.isRental as boolean | null ?? null;
    listing.price = Number(attributes.price);
    listing.soldPrice = attributes.soldPrice ? Number(attributes.soldPrice) : null;
    listing.status = attributes.status as string | null ?? null;
    listing.addressPath = attributes.addressPath as string | null ?? null;
    listing.isStandardized = attributes.isStandardized as string ?? null;
    listing.bedrooms = attributes.bedrooms as number ?? null;
    listing.bathrooms = attributes.bathrooms as number ?? null;
    listing.bedroomsPartial = attributes.bedroomsPartial as number | null ?? null;
    listing.bathroomsPartial = attributes.bathroomsPartial as number | null ?? null;
    listing.providerId = attributes.providerId as number | null ?? null;
    listing.isVow = attributes.isVow as boolean ?? null;
    listing.isItsoVow = attributes.isItsoVow as boolean ?? null;
    listing.isCrea = attributes.isCrea as boolean ?? null;
    listing.isImageReady = attributes.isImageReady as boolean ?? null;
    listing.imageUrls = attributes.imageUrls as string[] ?? null;
    listing.thumbnailUrls = attributes.thumbnailUrls as string[] ?? null;
    listing.position = attributes.position ? new Point(attributes.position as Record<string, unknown>) : null;
    listing.unitNumber = attributes.unitNumber as string | null ?? null;
    listing.mlsNum = attributes.mlsNum as string | null ?? null;
    listing.streetNumber = attributes.streetNumber as string ?? null;
    listing.streetName = attributes.streetName as string ?? null;
    listing.city = attributes.city as string | null ?? null;
    listing.province = attributes.province as string ?? null;
    listing.postalCode = attributes.postalCode as string ?? null;
    listing.openHouses = attributes.openHouses ? (attributes.openHouses as Record<string, unknown>[])?.map(o => new OpenHouse(o)) : null;
    listing.styleName = attributes.styleName as string ?? null;
    listing.imageUrl = attributes.imageUrl as string | null ?? null;
    listing.thumbnailUrl = attributes.thumbnailUrl as string ?? null;
    listing.path = attributes.path as string ?? null;
    listing.parking = attributes.parking ? Number(attributes.parking) : null;
    listing.addressSlug = attributes.addressSlug as string ?? null;
    listing.availableAt = attributes.availableAt as string | null ?? null;
    listing.#addedAt = new Date(attributes.addedAt as string);
    listing.#expiredAt = attributes.expiredAt ? new Date(attributes.expiredAt as string) : null;
    listing.#updatedOn = attributes.updatedOn ? new Date(attributes.updatedOn as string) : null;
    listing.#soldAt = attributes.soldAt ? new Date(attributes.soldAt as string) : null;
    listing.lastStatus = attributes.lastStatus as string | null ?? null;
    listing.squareFootage = attributes.squareFootage as Record<'min' | 'max', number> | null ?? null;
    listing.virtualTourUrl = attributes.virtualTourUrl as string | null ?? null;
    listing.levels = attributes.levels as string | null ?? null;
    listing.locker = attributes.locker as string | null ?? null;
    listing.maintenanceFees = attributes.maintenanceFees as number | null ?? null;
    listing.taxes = attributes.taxes as number | null ?? null;
    listing.#localeSensitiveDynamicFields.set('exterior', attributes.exterior as string | null ?? null);
    listing.garage = attributes.garage as string | null ?? null;
    listing.#localeSensitiveDynamicFields.set('driveway', attributes.driveway as string | null ?? null);
    listing.#localeSensitiveDynamicFields.set('heat', attributes.heat as string | null ?? null);
    listing.#localeSensitiveDynamicFields.set('ac', attributes.ac as string | null ?? null);
    listing.#localeSensitiveDynamicFields.set('basement', attributes.basement as string | null ?? null);
    listing.#localeSensitiveDynamicFields.set('heatingFuel', attributes.heatingFuel as string | null ?? null);
    listing.rooms = attributes.rooms ? (attributes.rooms as Record<string, unknown>[])?.map(r => new Room(r)) : null;
    listing.lotFrontage = attributes.lotFrontage as string | null ?? null;
    listing.lotDepth = attributes.lotDepth as string | null ?? null;
    listing.style = attributes.style as 'house' | 'condo' | 'land';
    listing.brokerage = attributes.brokerage as string | null ?? null;
    listing.brokerageEmail = attributes.brokerageEmail as string | null ?? null;
    listing.brokeragePhone = attributes.brokeragePhone as string | null ?? null;
    listing.disclaimer = attributes.disclaimer as string | null ?? null;
    listing.misc = attributes.misc as Miscellaneous | null ?? null;
    listing.neighbourhoodName = attributes.neighbourhoodName as string | null ?? null;
    listing.url = attributes.url as string | null ?? null;
    listing.imageDesc = attributes.imageDesc as string | null ?? null;
    listing.termsOfUseRequired = attributes.termsOfUseRequired as boolean | null ?? null;
    listing.statusId = attributes.statusId as number | null ?? null;
    listing.imageDescs = imageDescsMap;
    listing.neighbourhood = { ...neighbourhoodData } as Record<'id', number>;
    listing.pool = attributes.pool as string | null ?? null;
    listing.buyerInfo = buyerInfo ? {
      agentFirstName: buyerInfo.agentFirstName as string | null ?? null,
      agentLastName: buyerInfo.agentLastName as string | null ?? null,
      officeName: buyerInfo.officeName as string | null ?? null,
    } : null;
    listing.lastFetched = attributes.lastFetched ? new Date(attributes.lastFetched as string) : null;
    listing.expAgentId = attributes.expAgentId as number | null ?? null;
    listing.displayAddress = attributes.displayAddress as boolean | null ?? null;
    listing.standardStatus = standardStatusFromJSON(attributes.standardStatus as string);
    listing.propertyType = attributes.propertyType as typeof ResoPropertyType[keyof typeof ResoPropertyType]| null;
    listing.propertySubType = attributes.propertySubType as typeof ResoPropertySubType[keyof typeof ResoPropertySubType] | null;
    listing.listingAgent = attributes.listingAgent as string | null ?? null;
    listing.listingAgentContact = attributes.listingAgentContact as string | null ?? null;
    listing.originalMlsName = attributes.originalMlsName as string | null ?? null;
    
    const agentsInfo = attributes.agentsInfo || attributes['agents-info'];
    listing.#agentsInfo = agentsInfo ? (agentsInfo as AgentInfo[]) : null;
    const localeData = attributes.localeData as LocaleData | null ?? null;

    // Check if this is a Centris listing
    const isCentrisListing = listing.providerId === mlsProviderIds.centris;

    // We assume that if a listing doesn't have the locale data it's not a french listing.
    // In that case, we use the extras and description from the attributes as the english extras and description to
    // keep the consistency of the data.
    // Exception: For Centris listings, we don't use fallback - if English locale data doesn't exist, it stays null.
    if (localeData) {
      if (!Object.hasOwnProperty.call(localeData, SiteLanguage.English)) {
        //TODO: CLEAN UP thIS CODE ONCE WE BACKFILL THE LOCALE DATA FOR ALL LISTINGS IN PROD
        if (isCentrisListing) {
          // For Centris, don't add English fallback - keep only the existing locale data
          listing.localeData = localeData;
        } else {
          // For other providers, use root attributes as English fallback
          listing.localeData = {
            ...localeData,
            [SiteLanguage.English]: {
              extras: attributes.extras as string | null ?? null,
              description: attributes.description as string | null ?? null,
            },
          };
        }
      } else {
        listing.localeData = localeData;
      }
    } else {
      if (isCentrisListing) {
        // For Centris with no locale data, set to null
        listing.localeData = null;
      } else {
        // For other providers, use root attributes as English data
        listing.localeData = {
          [SiteLanguage.English]: {
            extras: attributes.extras as string | null ?? null,
            description: attributes.description as string | null ?? null,
          },
        };
      }
    }
    listing.applyListingRestrictions();
    return listing;
  }

  /**
   * Create a listing instance from a json object serialized using {@link toJSON}.
   *
   * Use {@link Listing.fromClientAppListing} to create a listing from a client-app listing object (received from the client-app JSON Api).
   *
   * @param json The json object to create a listing from.
   * @returns A new listing instance.
   */
  static fromJSON(json: ListingSerializableProperties) {
    const listing = new Listing(json.language ?? SiteLanguage.English);
    listing.id = json.id;
    listing.type = json.type ?? null;
    listing.isRental = json.isRental ?? null;
    listing.price = json.price ?? null;
    listing.soldPrice = json.soldPrice ?? null;
    listing.status = json.status ?? null;
    listing.addressPath = json.addressPath ?? null;
    listing.isStandardized = json.isStandardized;
    listing.bedrooms = json.bedrooms;
    listing.bathrooms = json.bathrooms;
    listing.bedroomsPartial = json.bedroomsPartial ?? null;
    listing.bathroomsPartial = json.bathroomsPartial ?? null;
    listing.providerId = json.providerId ?? null;
    listing.isVow = json.isVow;
    listing.isItsoVow = json.isItsoVow;
    listing.isCrea = json.isCrea;
    listing.isImageReady = json.isImageReady;
    listing.imageUrls = json.imageUrls;
    listing.thumbnailUrls = json.thumbnailUrls;
    listing.position = json.position ? new Point(json.position) : null;
    listing.unitNumber = json.unitNumber ?? null;
    listing.mlsNum = json.mlsNum ?? null;
    listing.streetNumber = json.streetNumber;
    listing.streetName = json.streetName;
    listing.city = json.city ?? null;
    listing.province = json.province;
    listing.postalCode = json.postalCode;
    listing.openHouses = json.openHouses ? (json.openHouses)?.map(r => new OpenHouse(r)) : null;
    listing.styleName = json.styleName;
    listing.imageUrl = json.imageUrl ?? null;
    listing.thumbnailUrl = json.thumbnailUrl;
    listing.path = json.path;
    listing.parking = json.parking ?? null;
    listing.addressSlug = json.addressSlug;
    listing.availableAt = json.availableAt ?? null;
    listing.#addedAt = json.addedAt ? new Date(json.addedAt as string) : null;
    listing.#expiredAt = json.expiredAt ? new Date(json.expiredAt as string) : null;
    listing.#updatedOn = json.updatedOn ? new Date(json.updatedOn as string) : null;
    listing.#soldAt = json.soldAt ? new Date(json.soldAt as string) : null;
    listing.lastStatus = json.lastStatus;
    listing.squareFootage = json.squareFootage ?? null;
    listing.virtualTourUrl = json.virtualTourUrl ?? null;
    listing.levels = json.levels ?? null;
    listing.locker = json.locker ?? null;
    listing.maintenanceFees = json.maintenanceFees ?? null;
    listing.taxes = json.taxes ?? null;
    listing.#localeSensitiveDynamicFields.set('exterior', json.exterior ?? null);
    listing.garage = json.garage ?? null;
    listing.#localeSensitiveDynamicFields.set('driveway', json.driveway ?? null);
    listing.#localeSensitiveDynamicFields.set('heat', json.heat ?? null);
    listing.#localeSensitiveDynamicFields.set('ac', json.ac ?? null);
    listing.#localeSensitiveDynamicFields.set('basement', json.basement ?? null);
    listing.#localeSensitiveDynamicFields.set('heatingFuel', json.heatingFuel ?? null);
    listing.rooms = json.rooms ? (json.rooms)?.map(r => new Room(r)) : null;
    listing.lotFrontage = json.lotFrontage ?? null;
    listing.lotDepth = json.lotDepth ?? null;
    listing.style = json.style;
    listing.brokerage = json.brokerage ?? null;
    listing.brokerageEmail = json.brokerageEmail ?? null;
    listing.brokeragePhone = json.brokeragePhone ?? null;
    listing.disclaimer = json.disclaimer ?? null;
    listing.misc = json.misc ?? null;
    listing.neighbourhoodName = json.neighbourhoodName ?? null;
    listing.url = json.url ?? null;
    listing.imageDesc = json.imageDesc ?? null;
    listing.termsOfUseRequired = json.termsOfUseRequired ?? null;
    listing.statusId = json.statusId ?? null;
    listing.imageDescs = json.imageDescs ? new Map(Object.entries(json.imageDescs)) : null;
    listing.neighbourhood = json.neighbourhood ?? null;
    listing.pool = json.pool ?? null;
    listing.buyerInfo = json.buyerInfo ? {
      agentFirstName: json.buyerInfo.agentFirstName ?? null,
      agentLastName: json.buyerInfo.agentLastName ?? null,
      officeName: json.buyerInfo.officeName ?? null,
    } : null;
    listing.lastFetched = json.lastFetched ? new Date(json.lastFetched as string) : null;
    listing.expAgentId = json.expAgentId ?? null;
    listing.displayAddress = json.displayAddress ?? null;
    listing.standardStatus = json.standardStatus ?? null;
    listing.propertyType = json.propertyType ?? null;
    listing.propertySubType = json.propertySubType ?? null;
    listing.listingAgent = json.listingAgent ?? null;
    listing.listingAgentContact = json.listingAgentContact ?? null;
    listing.originalMlsName = json.originalMlsName ?? null;
    listing.localeData = json.localeData ?? null;
    listing.#agentsInfo = json.agentsInfo ? (json.agentsInfo as AgentInfo[]) : null;

    // If we are reading a listing that doesn't have the locale data,
    // we assume it was serialized before localeData was added to Listing.
    // So we try to use the extras and description from the json object
    // to keep the consistency of the data.
    if (!listing.localeData) {
      listing.localeData = {
        [SiteLanguage.English]: {
          extras: (json as Record<string, unknown>).extras as string | null ?? null,
          description: (json as Record<string, unknown>).description as string | null ?? null,
        },
      };
    }
    listing.applyListingRestrictions();
    return listing;
  }
}

export type ClientAppListing = {
  id: string;
  type: 'listings';
  attributes?: Record<string, unknown>;
  relationships?: Record<string, unknown>;
};

/**
 * Check if an object has the properties associated with a client-app listing object (received from the client-app JSON Api).
 *
 * @param obj The object to check.
 * @returns `true` if the object is a client-app listing, `false` otherwise.
 */
export function isClientAppListing(obj: unknown): obj is ClientAppListing {
  return obj && typeof obj === 'object' && 'type' in obj && obj.type === 'listings';
}